import React from 'react';
import { ChevronRight, Loader, AlertCircle } from 'lucide-react';

interface ServiceRowProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  active: boolean;
  beta?: boolean;
  connected: boolean;
  expanded: boolean;
  saving?: boolean;
  providerName?: string;
  onToggle: () => void;
  onActivate?: () => void;
  children?: React.ReactNode;
}

export function ServiceRow({
  icon: Icon,
  title,
  description,
  active,
  beta,
  connected,
  expanded,
  saving,
  providerName = 'Google',
  onToggle,
  onActivate,
  children,
}: ServiceRowProps) {
  const canExpand = active && connected;

  return (
    <div className="border-b border-[rgba(55,53,47,0.06)] dark:border-white/[0.06] last:border-b-0">
      <div
        onClick={canExpand ? onToggle : undefined}
        className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${
          canExpand
            ? 'cursor-pointer hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-white/[0.03]'
            : 'cursor-default'
        }`}
      >
        <Icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-[#37352F] dark:text-white' : 'text-[#9B9A97] dark:text-white/30'}`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${active ? 'text-[#37352F] dark:text-white' : 'text-[#787774] dark:text-white/50'}`}>
              {title}
            </span>
            {beta && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 font-semibold uppercase tracking-wide">
                Beta
              </span>
            )}
          </div>
          {description && (
            <p className="text-xs text-[#787774] dark:text-white/40 mt-0.5 truncate hidden sm:block">
              {description}
            </p>
          )}
        </div>

        {saving && <Loader className="h-3.5 w-3.5 animate-spin text-[#787774]" />}

        {/* Status pill */}
        <span
          className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
            active
              ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400'
              : 'bg-[rgba(55,53,47,0.06)] dark:bg-white/5 text-[#787774] dark:text-white/40'
          }`}
        >
          {active ? 'Attivo' : 'Non attivo'}
        </span>

        {/* Activate button */}
        {!active && connected && onActivate && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onActivate();
            }}
            className="text-xs font-medium px-2.5 py-1 bg-[#37352F] dark:bg-white text-white dark:text-gray-900 rounded-md hover:bg-[#37352F]/90 dark:hover:bg-white/90 transition-colors whitespace-nowrap"
          >
            Attiva
          </button>
        )}

        {/* Connect notice */}
        {!active && !connected && (
          <span className="text-[11px] text-[#787774] dark:text-white/40 whitespace-nowrap hidden sm:inline">
            Connetti {providerName}
          </span>
        )}

        <ChevronRight
          className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${
            canExpand ? 'text-[#787774]' : 'text-[#9B9A97]/50'
          } ${expanded ? 'rotate-90' : ''}`}
        />
      </div>

      {/* Expanded content */}
      {expanded && canExpand && (
        <div className="ml-7 pl-4 pr-3 pb-4 pt-1 border-l border-[rgba(55,53,47,0.09)] dark:border-white/10 ml-7 mr-3 mb-2">
          {children}
        </div>
      )}

      {/* Warning if active but not connected */}
      {active && !connected && (
        <div className="mx-3 mb-2 mt-1 flex items-start gap-2 p-2.5 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
          <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            Servizio attivo ma account {providerName} non connesso. Connetti il tuo account dall'header per usarlo.
          </p>
        </div>
      )}
    </div>
  );
}
